// Data Models for Bharti Gold Business Franchise System

// User/Franchise Model
export class Franchise {
  constructor(data = {}) {
    this.id = data.id || null;
    this.franchiseName = data.franchiseName || '';
    this.ownerName = data.ownerName || '';
    this.email = data.email || '';
    this.phone = data.phone || '';
    this.address = data.address || '';
    this.city = data.city || '';
    this.state = data.state || '';
    this.pincode = data.pincode || '';
    this.gstNumber = data.gstNumber || '';
    this.licenseNumber = data.licenseNumber || '';
    this.establishedDate = data.establishedDate || new Date();
    this.status = data.status || 'active'; // active, inactive, suspended
    this.employeeCount = data.employeeCount || 0;
    this.monthlyRevenue = data.monthlyRevenue || 0;
    this.createdAt = data.createdAt || new Date();
    this.updatedAt = data.updatedAt || new Date();
  }
}

// Product Model
export class Product {
  constructor(data = {}) {
    this.id = data.id || null;
    this.name = data.name || '';
    this.category = data.category || ''; // gold, silver, diamond, platinum
    this.subCategory = data.subCategory || ''; // necklace, ring, bracelet, earrings, etc.
    this.weight = data.weight || 0;
    this.purity = data.purity || ''; // 22K, 24K, 18K, etc.
    this.price = data.price || 0;
    this.makingCharges = data.makingCharges || 0;
    this.gst = data.gst || 0;
    this.totalPrice = data.totalPrice || 0;
    this.stockQuantity = data.stockQuantity || 0;
    this.minStockLevel = data.minStockLevel || 0;
    this.images = data.images || [];
    this.description = data.description || '';
    this.sku = data.sku || '';
    this.barcode = data.barcode || '';
    this.hallmarkCertified = data.hallmarkCertified || false;
    this.createdAt = data.createdAt || new Date();
    this.updatedAt = data.updatedAt || new Date();
  }
}

// Order Model
export class Order {
  constructor(data = {}) {
    this.id = data.id || null;
    this.orderNumber = data.orderNumber || '';
    this.franchiseId = data.franchiseId || null;
    this.customerId = data.customerId || null;
    this.customerName = data.customerName || '';
    this.customerPhone = data.customerPhone || '';
    this.customerEmail = data.customerEmail || '';
    this.items = data.items || []; // Array of OrderItem
    this.subtotal = data.subtotal || 0;
    this.discount = data.discount || 0;
    this.gst = data.gst || 0;
    this.totalAmount = data.totalAmount || 0;
    this.paymentMethod = data.paymentMethod || ''; // cash, card, upi, neft, etc.
    this.paymentStatus = data.paymentStatus || 'pending'; // pending, partial, paid
    this.orderStatus = data.orderStatus || 'new'; // new, processing, completed, cancelled
    this.notes = data.notes || '';
    this.deliveryAddress = data.deliveryAddress || '';
    this.deliveryDate = data.deliveryDate || null;
    this.createdAt = data.createdAt || new Date();
    this.updatedAt = data.updatedAt || new Date();
  }
}

// Order Item Model
export class OrderItem {
  constructor(data = {}) {
    this.id = data.id || null;
    this.productId = data.productId || null;
    this.productName = data.productName || '';
    this.category = data.category || '';
    this.weight = data.weight || 0;
    this.purity = data.purity || '';
    this.quantity = data.quantity || 1;
    this.unitPrice = data.unitPrice || 0;
    this.makingCharges = data.makingCharges || 0;
    this.discount = data.discount || 0;
    this.totalPrice = data.totalPrice || 0;
  }
}

// Inventory Model
export class Inventory {
  constructor(data = {}) {
    this.id = data.id || null;
    this.franchiseId = data.franchiseId || null;
    this.productId = data.productId || null;
    this.productName = data.productName || '';
    this.category = data.category || '';
    this.currentStock = data.currentStock || 0;
    this.reservedStock = data.reservedStock || 0;
    this.availableStock = data.availableStock || 0;
    this.minStockLevel = data.minStockLevel || 0;
    this.maxStockLevel = data.maxStockLevel || 0;
    this.reorderPoint = data.reorderPoint || 0;
    this.lastRestocked = data.lastRestocked || null;
    this.stockValue = data.stockValue || 0;
    this.location = data.location || '';
    this.updatedAt = data.updatedAt || new Date();
  }
}

// Customer Model
export class Customer {
  constructor(data = {}) {
    this.id = data.id || null;
    this.name = data.name || '';
    this.email = data.email || '';
    this.phone = data.phone || '';
    this.alternatePhone = data.alternatePhone || '';
    this.address = data.address || '';
    this.city = data.city || '';
    this.state = data.state || '';
    this.pincode = data.pincode || '';
    this.dateOfBirth = data.dateOfBirth || null;
    this.anniversaryDate = data.anniversaryDate || null;
    this.gstNumber = data.gstNumber || '';
    this.panNumber = data.panNumber || '';
    this.totalPurchases = data.totalPurchases || 0;
    this.loyaltyPoints = data.loyaltyPoints || 0;
    this.customerType = data.customerType || 'regular'; // regular, vip, corporate
    this.notes = data.notes || '';
    this.createdAt = data.createdAt || new Date();
    this.updatedAt = data.updatedAt || new Date();
  }
}

// Employee Model
export class Employee {
  constructor(data = {}) {
    this.id = data.id || null;
    this.franchiseId = data.franchiseId || null;
    this.employeeCode = data.employeeCode || '';
    this.name = data.name || '';
    this.email = data.email || '';
    this.phone = data.phone || '';
    this.designation = data.designation || ''; // Manager, Sales Executive, Cashier, etc.
    this.department = data.department || ''; // Sales, Accounts, Operations, etc.
    this.joiningDate = data.joiningDate || new Date();
    this.salary = data.salary || 0;
    this.address = data.address || '';
    this.emergencyContact = data.emergencyContact || '';
    this.status = data.status || 'active'; // active, inactive, on-leave
    this.permissions = data.permissions || [];
    this.createdAt = data.createdAt || new Date();
    this.updatedAt = data.updatedAt || new Date();
  }
}

// Transaction Model
export class Transaction {
  constructor(data = {}) {
    this.id = data.id || null;
    this.franchiseId = data.franchiseId || null;
    this.transactionType = data.transactionType || ''; // sale, purchase, expense, return
    this.referenceNumber = data.referenceNumber || '';
    this.amount = data.amount || 0;
    this.paymentMethod = data.paymentMethod || '';
    this.description = data.description || '';
    this.category = data.category || '';
    this.status = data.status || 'completed'; // pending, completed, cancelled
    this.relatedOrderId = data.relatedOrderId || null;
    this.createdBy = data.createdBy || null;
    this.createdAt = data.createdAt || new Date();
  }
}

// Vendor/Supplier Model
export class Vendor {
  constructor(data = {}) {
    this.id = data.id || null;
    this.companyName = data.companyName || '';
    this.contactPerson = data.contactPerson || '';
    this.email = data.email || '';
    this.phone = data.phone || '';
    this.address = data.address || '';
    this.gstNumber = data.gstNumber || '';
    this.panNumber = data.panNumber || '';
    this.productCategories = data.productCategories || [];
    this.paymentTerms = data.paymentTerms || '';
    this.creditLimit = data.creditLimit || 0;
    this.currentBalance = data.currentBalance || 0;
    this.rating = data.rating || 0;
    this.status = data.status || 'active';
    this.notes = data.notes || '';
    this.createdAt = data.createdAt || new Date();
    this.updatedAt = data.updatedAt || new Date();
  }
}

// Analytics Model
export class Analytics {
  constructor(data = {}) {
    this.id = data.id || null;
    this.franchiseId = data.franchiseId || null;
    this.period = data.period || ''; // daily, weekly, monthly, yearly
    this.date = data.date || new Date();
    this.totalRevenue = data.totalRevenue || 0;
    this.totalOrders = data.totalOrders || 0;
    this.averageOrderValue = data.averageOrderValue || 0;
    this.totalCustomers = data.totalCustomers || 0;
    this.newCustomers = data.newCustomers || 0;
    this.topSellingProducts = data.topSellingProducts || [];
    this.categoryWiseSales = data.categoryWiseSales || {};
    this.paymentMethodBreakdown = data.paymentMethodBreakdown || {};
    this.inventoryTurnover = data.inventoryTurnover || 0;
    this.profitMargin = data.profitMargin || 0;
    this.createdAt = data.createdAt || new Date();
  }
}

// Helper function to serialize models for API
export const serializeModel = (model) => {
  const serialized = {};
  Object.keys(model).forEach(key => {
    if (model[key] !== null && model[key] !== undefined) {
      serialized[key] = model[key];
    }
  });
  return serialized;
};

// Helper function to parse API response to models
export const parseApiResponse = (ModelClass, data) => {
  if (Array.isArray(data)) {
    return data.map(item => new ModelClass(item));
  }
  return new ModelClass(data);
};